import { useEffect, useId, useMemo, useRef, type CSSProperties } from 'react';
import type { LessonListResponseItem } from '../api/courseRecordApi';
import { WorkflowStatusBadge, deriveBadgeState } from './WorkflowStatusBadge';
import { formatLessonDuration } from '../utils/formatLessonDuration';
import { formatLessonHumanId } from '../utils/humanIds';
import { StandardTitleLine } from './StandardTitleLine';

interface AddLessonModalProps {
  styles: Record<string, CSSProperties>;
  /** Flattened lesson library (all loaded pages). */
  lessons: LessonListResponseItem[];
  /** Lesson ids already in the course; shown inline as disabled. */
  selectedLessonIds: Set<string>;
  /** Total matching lessons on the server (drives the result count). */
  resultCount: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onFetchNextPage: () => void;
  onAdd: (lessonId: string) => void;
  onClose: () => void;
  onCreateLesson: () => void;
}

function RefreshIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M13 3.5v3h-3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.4 6.5A4.8 4.8 0 1 0 13 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Add Lesson modal for the course editor: server-search + infinite-scroll lesson
 * library. Already-in-course lessons render inline, disabled. Data fetching lives
 * in the parent (CourseEditorPage) and is passed in; this component owns only the
 * scroll/intersection mechanics and presentation.
 */
export function AddLessonModal({
  styles,
  lessons,
  selectedLessonIds,
  resultCount,
  searchValue,
  onSearchChange,
  isLoading,
  isRefreshing,
  onRefresh,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  onAdd,
  onClose,
  onCreateLesson,
}: AddLessonModalProps) {
  const titleId = useId();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          onFetchNextPage();
        }
      },
      { root, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onFetchNextPage]);

  const availableLessons = useMemo(
    () => lessons.filter((lesson) => !selectedLessonIds.has(lesson.id)),
    [lessons, selectedLessonIds]
  );
  const alreadyAddedLessons = useMemo(
    () => lessons.filter((lesson) => selectedLessonIds.has(lesson.id)),
    [lessons, selectedLessonIds]
  );

  return (
    <div style={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle} id={titleId}>Add lesson to course</h3>
          <button
            type="button"
            style={styles.modalCloseButton}
            onClick={onClose}
            aria-label="Close add lesson modal"
          >
            ×
          </button>
        </div>
        <div ref={scrollRef} style={{ ...styles.modalBody, ...styles.addLessonModalBody }}>
          <div style={styles.modalSearchToolbar}>
            <input
              type="search"
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search ID, title, or description..."
              style={styles.modalSearchInput}
              aria-label="Search lessons by ID, title, or description"
              autoFocus
            />
            <span style={styles.modalResultsCount}>
              {resultCount} {resultCount === 1 ? 'result' : 'results'}
            </span>
            <button
              type="button"
              style={{
                ...styles.refreshLessonsButton,
                ...(isRefreshing ? styles.disabledButton : {}),
              }}
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label="Refresh lessons"
            >
              <RefreshIcon />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div style={styles.modalLessonContent}>
            <div style={styles.librarySectionLabel}>All lessons</div>
            {isLoading ? (
              <div style={styles.mutedText}>Loading lessons...</div>
            ) : availableLessons.length === 0 ? (
              <div style={styles.mutedText}>No matching lessons available.</div>
            ) : (
              <>
                {availableLessons.map((lesson) => (
                  <div key={lesson.id} style={styles.libraryRow}>
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.lessonTitle}>{lesson.title || 'Untitled Lesson'}</div>
                      <StandardTitleLine standardTitle={lesson.standardTitle} style={styles.standardTitleLine} />
                      {lesson.description ? (
                        <div style={styles.libraryRowDescription}>{lesson.description}</div>
                      ) : null}
                      <div style={styles.lessonMeta}>
                        {formatLessonHumanId(lesson.numericId, lesson.id)} · {formatLessonDuration(lesson.estimatedTimeMinutes)}
                      </div>
                    </div>
                    <WorkflowStatusBadge state={deriveBadgeState(lesson.workflowState, lesson.publishLifecycle)} />
                    <button
                      type="button"
                      style={styles.smallButton}
                      onClick={() => onAdd(lesson.id)}
                    >
                      + Add
                    </button>
                  </div>
                ))}
                {alreadyAddedLessons.map((lesson) => (
                  <div key={lesson.id} style={{ ...styles.libraryRow, ...styles.libraryRowDisabled }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.lessonTitle}>{lesson.title || 'Untitled Lesson'}</div>
                      <StandardTitleLine standardTitle={lesson.standardTitle} style={styles.standardTitleLine} />
                      {lesson.description ? (
                        <div style={styles.libraryRowDescription}>{lesson.description}</div>
                      ) : null}
                      <div style={styles.lessonMeta}>
                        {formatLessonHumanId(lesson.numericId, lesson.id)} · {formatLessonDuration(lesson.estimatedTimeMinutes)}
                      </div>
                    </div>
                    <WorkflowStatusBadge state={deriveBadgeState(lesson.workflowState, lesson.publishLifecycle)} />
                    <span style={styles.mutedText}>Added ✓</span>
                  </div>
                ))}
                <div ref={sentinelRef} aria-hidden="true" />
                {isFetchingNextPage && (
                  <div style={styles.mutedText}>Loading more...</div>
                )}
              </>
            )}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <span style={styles.mutedText}>Can't find what you need?</span>
          <button type="button" style={styles.secondaryButton} onClick={onCreateLesson}>
            + Create new lesson
          </button>
        </div>
      </div>
    </div>
  );
}
